import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useInternalLink } from '../../hooks/useInternalLink';
import { useFetchBrowse } from '../../hooks/useFetchData';

const StyledLink = styled(Link)`
  font-size: 15px;
  line-height: ${({ theme }) => theme.lineHeights.xxs};
  color: ${({ theme }) => theme.colors.elvitaWebPurple};
  font-weight: 300;

  &:hover {
    text-decoration: underline;
  }

  &:active {
    color: ${({ theme }) => theme.colors.elvitaWebPink};
    text-decoration: none;
  }
`;

type AnchorProps = {
  href: string;
  children: React.ReactNode;
  data: string;
};

const Anchor: FC<AnchorProps> = ({ href, data, children }) => {
  const { internalLink } = useInternalLink();
  const { data: browseData } = useFetchBrowse();
  const [linkTo, setLinkTo] = useState(href);

  useEffect(() => {
    if (!href || !browseData?.data) return;

    const isBrowseTarget = (nodes: Elvita.Node[]): boolean => {
      for (const node of nodes || []) {
        if (
          node.topicId === href &&
          node.children &&
          node.children.length > 0
        ) {
          return true;
        }

        if (node.children && isBrowseTarget(node.children)) {
          return true;
        }
      }
      return false;
    };

    const findTopLevelParent = (nodes: Elvita.Node[]): string | null => {
      for (const node of nodes || []) {
        if (node.topicId === href) {
          return null;
        }

        if (node.children?.some(child => child.topicId === href)) {
          return node.topicId;
        }

        const deepResult = node.children && findTopLevelParent(node.children);
        if (deepResult) return deepResult;
      }
      return null;
    };

    const isBrowse = isBrowseTarget(browseData.data);

    if (isBrowse) {
      const topLevelId = findTopLevelParent(browseData.data);
      const linkPath = topLevelId
        ? `/browse/${topLevelId}/${href}`
        : `/browse/${href}`;

      setLinkTo(internalLink(linkPath));
    } else {
      setLinkTo(internalLink(`/topics/${href}`));
    }
  }, [browseData, href, internalLink]);

  return <StyledLink to={href}>{data}</StyledLink>;
};

export default Anchor;
